import { motion } from "framer-motion";
import {
  AlertTriangle,
  Lightbulb,
  Layers,
  Workflow,
  Plug,
  TrendingUp,
} from "lucide-react";
import { Eyebrow, fadeUp, Chip, Screenshot } from "@/components/landing/shared";
import { BrowserShowcase } from "@/components/landing/Showcase";

const SUBSECTIONS = [
  {
    icon: AlertTriangle,
    label: "Il problema",
    body: "Un territorio ricco ma frammentato: strutture ricettive, ristoranti ed esperienze gestiti su canali separati. Nessun luogo unico dove il turista possa comporre e prenotare l'intera vacanza, e nessuno strumento per valorizzare e fidelizzare.",
  },
  {
    icon: Lightbulb,
    label: "La soluzione",
    body: "Un unico sistema dove il turista compone la vacanza — soggiorni, ristoranti, esperienze e prodotti tipici — prenota e accumula punti. Gli operatori del territorio pubblicano e gestiscono la propria attività da un'area dedicata.",
  },
  {
    icon: Layers,
    label: "L'architettura",
    body: "Frontend React, backend FastAPI in Python e database MongoDB. Autenticazione e accessi basati su ruoli (turista, partner, amministrazione), con API REST che collegano ricerca, prenotazioni, wallet punti e area riservata.",
  },
  {
    icon: Workflow,
    label: "I workflow",
    body: "Il partner registra la propria attività e pubblica le offerte. Il turista cerca, compone la vacanza e prenota. Ogni acquisto alimenta il wallet punti e i livelli card (Bronzo, Argento, Oro) e contribuisce al progetto del territorio.",
  },
  {
    icon: Plug,
    label: "Le integrazioni",
    body: "Mappe e geolocalizzazione con Google Maps, carrello e checkout, sistema punti/loyalty e gestione multilingua — tutto all'interno della stessa piattaforma.",
  },
  {
    icon: TrendingUp,
    label: "Il risultato",
    body: "Un marketplace territoriale unico, con dashboard per turisti e per partner, un sistema di fidelizzazione a livelli e un modello di contributo che restituisce valore al territorio.",
  },
];

const STACK = [
  "React",
  "FastAPI",
  "MongoDB",
  "REST API",
  "Python",
  "Authentication",
  "Role-based access",
  "Google Maps",
];

export default function CaseStudySila() {
  return (
    <section
      id="lavori"
      className="relative scroll-mt-20 border-t border-black/[0.08] py-16 md:py-24 lg:py-28"
    >
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <Eyebrow>Case study · 01</Eyebrow>
          <h2 className="mt-4 text-2xl font-semibold leading-tight tracking-[-0.03em] text-[color:var(--text-primary)] sm:text-3xl lg:text-[2.6rem]">
            Non ti racconto cosa potrei costruire.{" "}
            <span className="text-emerald-600">
              Ti faccio vedere cosa ho già costruito.
            </span>
          </h2>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="font-display text-lg font-semibold text-[color:var(--text-primary)]">
              Sila Experiences
            </span>
            <span className="h-4 w-px bg-black/15" />
            <span className="text-sm text-tertiary">
              Piattaforma turistica territoriale — Parco Nazionale della Sila, Calabria
            </span>
          </div>
        </motion.div>

        {/* Screenshots */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12"
        >
          <div className="lg:col-span-8">
            <BrowserShowcase
              src="/projects/sila-full.jpg"
              url="silaexperiences.it"
              alt="Sila Experiences — anteprima della piattaforma"
              caption="Anteprima live — home, ricerca e composizione della vacanza (soggiorni, ristoranti, esperienze, shop)"
              scrollMs={11000}
            />
          </div>
          <div className="flex items-center lg:col-span-4">
            <Screenshot
              src="/projects/sila-loyalty.jpg"
              alt="Sila Experiences — wallet punti e livelli card"
              caption="Wallet punti e livelli card: Bronzo, Argento, Oro"
            />
          </div>
        </motion.div>

        {/* Subsections */}
        <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-black/10 bg-black/[0.03] sm:grid-cols-2 lg:grid-cols-3">
          {SUBSECTIONS.map((s, i) => (
            <motion.div
              key={s.label}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              variants={fadeUp}
              custom={i}
              className="bg-[var(--ink-900)] p-6"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-400/25 bg-emerald-600/10 text-emerald-700">
                <s.icon className="h-4 w-4" />
              </span>
              <h3 className="mt-4 text-base font-semibold text-[color:var(--text-primary)]">
                {s.label}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-secondary">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Stack */}
        <div className="mt-8 flex flex-wrap items-center gap-2.5">
          <span className="mr-1 text-xs uppercase tracking-wide text-tertiary">
            Stack tecnico
          </span>
          {STACK.map((t) => (
            <Chip key={t}>{t}</Chip>
          ))}
        </div>
      </div>
    </section>
  );
}
